import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RefreshCw, Search, RotateCcw } from 'lucide-react';
import { Clinic, Facility } from '@/services/features/appointmentSlotsService.ts';

interface AppointmentSlotsSearchFormProps {
  // Clinic and facility data
  clinics: Clinic[];
  facilities: Facility[];
  selectedClinic: string;
  selectedFacility: string;
  facilitySearchQuery: string;
  filterClinicSearchQuery: string;
  isLoadingFacilities: boolean;
  isFilterApplied: boolean;
  isLoading: boolean;
  filteredAppointmentsCount: number;

  // Handlers
  onClinicChange: (clinicName: string) => void;
  onFacilityChange: (facilityName: string) => void;
  onFacilitySearchChange: (query: string) => void;
  onFilterClinicSearchChange: (query: string) => void;
  onOpenFilterDialog: () => void;
  onDeleteAll: () => void;
  onResetFilters: () => void;
}

const AppointmentSlotsSearchForm: React.FC<AppointmentSlotsSearchFormProps> = ({
  clinics,
  facilities,
  selectedClinic,
  selectedFacility,
  facilitySearchQuery,
  filterClinicSearchQuery,
  isLoadingFacilities,
  isFilterApplied,
  isLoading,
  filteredAppointmentsCount,
  onClinicChange,
  onFacilityChange,
  onFacilitySearchChange,
  onFilterClinicSearchChange,
  onOpenFilterDialog,
  onDeleteAll,
  onResetFilters,
}) => {
  // Filter clinics for search
  const filteredClinicsForFilter = clinics.filter(clinic =>
    clinic.clinic_name.toLowerCase().includes(filterClinicSearchQuery.toLowerCase())
  );

  return (
    <div className="bg-white border-b border-gray-100 px-8 py-6">
      <div className="max-w-7xl mx-auto">

        {/* Row 1: Labels */}
        <div className="grid grid-cols-3 gap-6 mb-4">
          <div className="text-sm font-medium text-gray-700">
            Select Clinic
          </div>
          <div className="text-sm font-medium text-gray-700">
            Select Facility
          </div>
          <div className="text-sm font-medium text-gray-700">
            Select Slot
          </div>
        </div>

        {/* Row 2: Controls */}
        <div className="grid grid-cols-3 gap-6">

          {/* Column 1: Clinic Dropdown */}
          <div>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start">
                  <Search className="mr-2 h-4 w-4" />
                  {selectedClinic || "Choose a clinic"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0" align="start">
                <div className="p-2">
                  <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search clinics..."
                      value={filterClinicSearchQuery}
                      onChange={(e) => onFilterClinicSearchChange(e.target.value)}
                      className="pl-10 h-8"
                    />
                  </div>
                </div>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  <div
                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                    onClick={() => onClinicChange("all")}
                  >
                    All Clinics
                  </div>
                  {filteredClinicsForFilter.map((clinic) => (
                    <div
                      key={clinic.clinic_id}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => onClinicChange(clinic.clinic_name)}
                    >
                      {clinic.clinic_name}
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Column 2: Facility Dropdown */}
          <div>
            {selectedClinic ? (
              <Select value={selectedFacility} onValueChange={onFacilityChange}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Choose a facility" />
                </SelectTrigger>
                <SelectContent>
                  <div className="p-2">
                    <div className="relative mb-2">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Search facilities..."
                        value={facilitySearchQuery}
                        onChange={(e) => onFacilitySearchChange(e.target.value)}
                        className="pl-10 h-8"
                      />
                    </div>
                  </div>
                  <SelectItem value="all">All Facilities</SelectItem>
                  {isLoadingFacilities ? (
                    <SelectItem value="loading" disabled>Loading facilities...</SelectItem>
                  ) : (
                    facilities
                      .filter(facility =>
                        facility.facility_name.toLowerCase().includes(facilitySearchQuery.toLowerCase())
                      )
                      .map((facility) => (
                        <SelectItem key={facility.facility_name} value={facility.facility_name}>
                          {facility.facility_name}
                        </SelectItem>
                      ))
                  )}
                </SelectContent>
              </Select>
            ) : (
              <Button variant="outline" className="w-full" disabled>
                Select clinic first
              </Button>
            )}
          </div>

          {/* Column 3: Select Slot Button & Actions */}
          <div className="flex items-center space-x-2">
            {selectedFacility ? (
              <>
                <Button
                  onClick={onOpenFilterDialog}
                  className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white flex-1"
                >
                  Select Slot
                </Button>

                {isFilterApplied && filteredAppointmentsCount > 0 && (
                  <Button
                    onClick={onDeleteAll}
                    variant="destructive"
                    size="sm"
                  >
                    Delete All
                  </Button>
                )}

                <Button
                  onClick={onResetFilters}
                  variant="outline"
                  size="icon"
                  disabled={isLoading}
                  className="text-blue-600 border-blue-600 hover:bg-blue-50"
                  title="Reset Filters"
                >
                  <RotateCcw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                </Button>
              </>
            ) : (
              <Button variant="outline" className="w-full" disabled>
                Select facility first
              </Button>
            )}
          </div>

        </div>

      </div>
    </div>
  );
};

export default AppointmentSlotsSearchForm;